import { useToolConfirmationNotifications } from "@/hooks/useToolConfirmationNotifications";
import {
  Message,
  MessagesResponse,
  ToolCall,
  UpdateToolConfirmationPayload,
  UpdateToolConfirmationResponse,
} from "@cohesive/api-client";
import {
  InfiniteData,
  QueryClient,
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { orgAPI } from "./api";

export function useUpdateToolConfirmation(
  options?: UseMutationOptions<
    UpdateToolConfirmationResponse,
    AxiosError,
    {
      flowSessionId: string;
      flowMessageId: string;
      toolCallId: string;
      payload: UpdateToolConfirmationPayload;
    },
    {
      previousMessages?: InfiniteData<MessagesResponse>;
    }
  >,
) {
  const queryClient = useQueryClient();
  const { deleteForSession } = useToolConfirmationNotifications();
  return useMutation<
    UpdateToolConfirmationResponse,
    AxiosError,
    {
      flowSessionId: string;
      flowMessageId: string;
      toolCallId: string;
      payload: UpdateToolConfirmationPayload;
    },
    {
      previousMessages?: InfiniteData<MessagesResponse>;
    }
  >({
    // Spread caller options FIRST so the hook's own handlers (defined below)
    // always run — the caller passes its own onSuccess/onError, and if these
    // were spread last they'd replace ours (dropping the notification delete,
    // the cache invalidate, and the error rollback). We compose the caller's
    // callbacks in explicitly instead.
    ...options,
    mutationFn: async ({ flowSessionId, flowMessageId, toolCallId, payload }) =>
      await orgAPI.toolConfirmations.update(
        flowSessionId,
        flowMessageId,
        toolCallId,
        payload,
      ),
    onMutate: async (variables) => {
      const { flowSessionId, flowMessageId, toolCallId, payload } = variables;
      // Compose the caller's onMutate too (options is spread first, so our
      // handlers are defined here); merge its context so our onError/onSuccess
      // still see previousMessages.
      const callerContext = await options?.onMutate?.(variables);

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ["flowMessages", flowSessionId],
      });

      // Snapshot the previous value
      const previousMessages = queryClient.getQueryData<
        InfiniteData<MessagesResponse>
      >(["flowMessages", flowSessionId]);

      // Optimistically update the message's tool call status
      queryClient.setQueryData<InfiniteData<MessagesResponse>>(
        ["flowMessages", flowSessionId],
        (old) => {
          if (!old) return old;

          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: {
                ...page.data,
                flow_messages: page.data.flow_messages.map(
                  (message: Message) => {
                    if (message.id !== flowMessageId) return message;

                    return {
                      ...message,
                      tool_calls: message.tool_calls?.map(
                        (toolCall: ToolCall) => {
                          if (toolCall.id !== toolCallId) return toolCall;
                          return {
                            ...toolCall,
                            user_confirmation_status:
                              payload.user_confirmation_status === "confirmed"
                                ? "confirmed-processing"
                                : "rejected",
                          };
                        },
                      ),
                    };
                  },
                ),
              },
            })),
          };
        },
      );

      return { ...callerContext, previousMessages };
    },
    onError: (error, variables, context) => {
      // If the mutation fails, roll back to the previous value.
      if (context?.previousMessages) {
        queryClient.setQueryData(
          ["flowMessages", variables.flowSessionId],
          context.previousMessages,
        );
      }
      options?.onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      // The chat's $toolConfirmation notification is collapsed (one item for all
      // the chat's pending calls), so only clear it once none remain pending —
      // resolving one call while others still need approval must keep the nudge.
      // The optimistic update above has already flipped this call off "pending",
      // so an all-resolved result here means this was the last one.
      if (allToolConfirmationsResolved(queryClient, variables.flowSessionId)) {
        deleteForSession(variables.flowSessionId);
      }

      queryClient.invalidateQueries({
        queryKey: ["flowMessages", variables.flowSessionId],
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Whether the flow session has no tool call left awaiting user approval, read
 * from the optimistically-updated flowMessages cache. Gates deletion of the
 * chat's collapsed $toolConfirmation notification: we clear it only once every
 * call is resolved. Returns false when the messages aren't cached, so an
 * "unknown" state never triggers a premature delete.
 */
function allToolConfirmationsResolved(
  queryClient: QueryClient,
  flowSessionId: string,
): boolean {
  const data = queryClient.getQueryData<InfiniteData<MessagesResponse>>([
    "flowMessages",
    flowSessionId,
  ]);
  if (!data) return false;

  return !data.pages.some((page) =>
    page.data.flow_messages.some((message: Message) =>
      message.tool_calls?.some(
        (toolCall: ToolCall) => toolCall.user_confirmation_status === "pending",
      ),
    ),
  );
}
